/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import User from "../../../models/userModel";
import {copyToClipboard, getSearchParams, navigate, ResponseData, showDialog} from '@dvrd/dvr-controls';
import AdminUsers from "./adminUsers";
import {AdminContext} from "../../misc/adminContext";
import {sendGaPageView} from "../../../util/analytics";
import {onSetCrumbs} from "../../../util/events";
import {ActivateHandle, ContextShape, SubscriptionStats, UserFilter} from "../../../util/interfaces";
import SubscriptionStatsController from "../../subscription/stats/subscriptionStatsController";
import {onShowConfirmSnackbar} from "../../../util/renderUtil";
import UsersFilterController, {emptyFilter, isFilterActive} from "./filter/usersFilterController";
import {getAdminSubscriptionStats} from "../../../util/requestSender";
import PlatformChurnController from "../churn/platformChurnController";
import {getDefaultRoute} from "../../../util/accessUtil";
import {AppContext} from "../../misc/appContext";
import {ADMIN_ROUTE, ADMIN_USER_ROUTE, ADMIN_USERS_ROUTE} from "../../../util/constants";
import {setIsAdminLogin} from "../../../util/adminUtil";

export const USERS_LIMIT = 10;
const emptyStats: SubscriptionStats = {active: 0, free: 0, inactive: 0, trialing: 0, canceled: 0, expired: 0};
export default function AdminUsersController() {
    const adminContext = useContext(AdminContext);
    const context = useContext(AppContext);
    const params = getSearchParams();
    const currentPage = Number(params.get('pagina') ?? 1) - 1;
    const [page, setPage] = useState(currentPage);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState(params.get('zoekterm') ?? '');
    const [users, setUsers] = useState<User[]>([]);
    const [total, setTotal] = useState(0);
    const [subStats, setSubStats] = useState<SubscriptionStats>(emptyStats);
    const [activeFilter, setActiveFilter] = useState<UserFilter>(emptyFilter);
    const searchDebounce = useRef<number | null>(null);
    const subStatsRef = useRef<SubscriptionStatsController>(null);
    const abortController = useRef<AbortController | null>(null);
    const filterRef = useRef<ActivateHandle>(null);
    const churnRef = useRef<ActivateHandle>(null);

    function onClickChurn() {
        churnRef.current?.activate();
    }

    function onCopyId(user: User) {
        return function() {
            copyToClipboard(user.id).then(() => {
                onShowConfirmSnackbar('ID gekopieerd naar het klembord')
            }).catch(() => {
                showDialog('Het kopiëren van het ID is niet gelukt.')
            });
        }
    }

    function onChangeSearch(value: string) {
        value = value.trim();
        setSearch(value);
    }

    function onChangePage(nextPage: number) {
        return function() {
            setParams(nextPage, search);
            setPage(nextPage);
        }
    }

    function onEnterSearch() {
        clearTimeout();
        const page = 0;
        setParams(page, search);
        loadUsers(true, undefined, page);
    }

    function onOpenFilter() {
        filterRef.current?.activate();
    }

    function onSubmitFilter(filter: UserFilter) {
        clearTimeout();
        setActiveFilter(filter);
        loadUsers(true, undefined, undefined, filter);
    }

    function onResetFilter() {
        clearTimeout();
        setActiveFilter(emptyFilter);
        loadUsers(true, undefined, undefined, emptyFilter);
    }

    function onClickEdit(user: User) {
        return function() {
            adminContext.onChangeContext({user}, () => {
                navigate(ADMIN_USER_ROUTE);
            })
        }
    }

    function onLoginUser(user: User) {
        return function() {
            abortCurrent();
            setLoading(true);
            window.setTimeout(() => {
                abortController.current = user.adminLogin((user: User, _: boolean, data: ResponseData) => {
                    setLoading(false);
                    if (data.success) {
                        setIsAdminLogin(true);
                        const account = user.accounts.length ? user.accounts[0] : undefined;
                        context.onChangeContext({user, account, accounts: user.accounts},
                            (context: ContextShape) => {
                                const {account} = context.accountContext;
                                const defaultRoute = getDefaultRoute(account?.userRights, account?.userRole);
                                navigate(defaultRoute);
                            });
                    } else showDialog(data.message ?? 'Het inloggen is niet gelukt.', 'Inloggen mislukt');
                });
            });
        }
    }

    function onOpenSubStats() {
        subStatsRef.current?.activate();
    }

    function setParams(page: number, search: string) {
        const {origin, pathname} = window.location;
        let url = `${origin}${pathname}?pagina=${page + 1}`;
        if (search) url += `&zoekterm=${search}`;
        history.pushState({}, '', url);
    }

    function clearTimeout() {
        if (searchDebounce.current) {
            window.clearTimeout(searchDebounce.current);
            searchDebounce.current = null;
        }
    }

    function loadUsers(abort: boolean = true, useSearch?: string, usePage?: number, filter?: UserFilter) {
        if (abort)
            abortCurrent();
        setLoading(true);
        window.setTimeout(() => {
            abortController.current = User.getAdminUsers(USERS_LIMIT, usePage ?? page, useSearch ?? search, filter,
                (users: User[], success: boolean, data: ResponseData) => {
                    abortController.current = null;
                    setLoading(false);
                    if (data.success) {
                        setUsers(users);
                        setTotal(data.total);
                    } else showDialog(data.message ?? 'Het laden van de gebruikers is niet gelukt.', 'Laden mislukt');
                });
        });
    }

    function loadSubscriptionStats() {
        getAdminSubscriptionStats({
            callback: (data: ResponseData) => {
                if (data.success) setSubStats(data.user_stats);
            }
        });
    }

    const abortCurrent = useCallback(() => {
        const controller = abortController.current;
        if (!controller) return;
        controller.abort();
        // Reset the abort controller to prevent auto-canceling next request
        abortController.current = new AbortController();
    }, [abortController]);

    useEffect(() => {
        loadUsers();
    }, [page]);

    useEffect(() => {
        sendGaPageView('/admin/gebruikers');
        onSetCrumbs([{label: 'Admin', route: ADMIN_ROUTE}, {label: 'Gebruikers', route: ADMIN_USERS_ROUTE}]);
        loadSubscriptionStats();
        return function() {
            clearTimeout();
            abortCurrent();
        }
    }, []);

    const hasActiveFilter = isFilterActive(activeFilter);

    return (
        <>
            <AdminUsers onChangeSearch={onChangeSearch} onClickEdit={onClickEdit} onChangePage={onChangePage}
                        onLoginUser={onLoginUser} onSearch={onEnterSearch} onSubStats={onOpenSubStats}
                        onCopyId={onCopyId} onOpenFilter={onOpenFilter} onResetFilter={onResetFilter}
                        onClickChurn={onClickChurn} users={users} hasActiveFilter={hasActiveFilter}
                        loading={loading} page={page} search={search} total={total} subStats={subStats}/>
            <SubscriptionStatsController ref={subStatsRef}/>
            <UsersFilterController ref={filterRef} onSubmit={onSubmitFilter}/>
            <PlatformChurnController ref={churnRef}/>
        </>
    )
}